import Box from 'components/layouts/Box';
import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Text from 'components/commons/Text';
import { Row } from 'saladsoft-primitive';
import { FormControlLabel, IconButton, Switch } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ServiceHelper from 'stores/ServiceHelper';
import { useStateContext } from 'providers/usePageState';
import { AmenityModel, TagModel } from 'types/CommonTypes';
import { useStoreWrapper } from 'stores/StoreHelper';
import { toast } from 'react-toastify';
import { removeHtmlTags } from 'libs/helper/utils';
import {
  AdminLandmarkDto,
  AdminListingDto,
  AdminListingImageDto,
  AdminSystemLocaleDto,
  AdminOptionDto,
} from 'services/data-contracts';

const ListingMenus = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { uiStore } = useStoreWrapper();
  const { listingId } = useParams();
  const { setListing, listing, receiver } = useStateContext();

  const [amenities, setAmenities] = useState<AmenityModel[]>([]);
  const [photos, setPhotos] = useState<AdminListingImageDto[]>([]);
  const [tags, setTags] = useState<TagModel[]>([]);
  const [landmarks, setLandmarks] = useState<AdminLandmarkDto[]>([]);
  const [recommendListings, setRecommendListings] = useState<AdminListingDto[]>([]);
  const [languages, setLanguages] = useState<AdminSystemLocaleDto[]>([]);
  const [options, setOptions] = useState<AdminOptionDto[]>([]);

  const routes = [
    {
      route: `/admin/listings/${listingId}/photos`,
      name: '사진',
      children: photos?.length > 0 ? `${photos?.length}장의 사진 등록` : '등록된 사진이 없습니다.',
    },
    {
      route: `/admin/listings/${listingId}/name`,
      name: '숙소명',
      children: (
        <div>
          <div>{listing?.localizedName?.ko}</div>
          <div>{listing?.localizedTitle?.ko}</div>
        </div>
      ),
    },
    // { route: `/admin/listings/${listingId}/type`, name: '숙소유형', children: '독채 한옥' },
    {
      route: `/admin/listings/${listingId}/guest-info`,
      name: '게스트 및 시설정보',
      children: `기준 ${listing?.minGuest || 0}인, 최대 ${listing?.maxGuest || 0}인`,
    },
    {
      route: `/admin/listings/${listingId}/description`,
      name: '설명',
      children: removeHtmlTags(listing?.localizedDesc?.ko, 100),
    },
    {
      route: `/admin/listings/${listingId}/location`,
      name: '위치',
      children: (
        <div>
          <div>{listing?.region?.localizedName?.ko}</div>
          <div>{listing?.localizedAddress?.ko}</div>
        </div>
      ),
    },
    {
      route: `/admin/listings/${listingId}/amenities`,
      name: '편의시설',
      children:
        amenities?.length === 0
          ? '등록된 편의시설이 없습니다.'
          : amenities.length > 10
            ? `${amenities
              .slice(0, 10)
              .map((amenity) => amenity.localizedName?.ko)
              .join(', ')} 외 ${amenities.length - 10}개`
            : amenities.map((amenity) => amenity.localizedName?.ko).join(', '),
    },

    // { route: `/admin/listings/${listingId}/directions`, name: '찾아오는 길', children: '' },
    {
      route: `/admin/listings/${listingId}/tags`,
      name: '태그',
      children:
        tags?.length === 0
          ? '등록된 편의시설이 없습니다.'
          : tags.length > 10
            ? `${tags
              .slice(0, 10)
              .map((tag) => tag.localizedName?.ko)
              .join(', ')} 외 ${tags.length - 10}개`
            : tags.map((tag) => tag.localizedName?.ko).join(', '),
    },
    // {
    //   // route: `/admin/settings/${listingId}/settings`,
    //   name: '노출 설정',
    //   children: <IsActiveComponent />,
    // },
    {
      route: `/admin/listings/${listingId}/additional-options`,
      name: '옵션설정',
      children: (
        <div>
          <div>
            {options.length > 0 ? (
              <div>
                {options
                  .slice(0, 1)
                  .map((opt) => opt.localizedName?.ko)
                  .join(', ')}
                {options.length > 1 && ` 외 ${options.length - 1}개`}
              </div>
            ) : (
              <div>현재 등록된 옵션이 없습니다.</div>
            )}
          </div>
        </div>
      ),
    },
    {
      route: `/admin/listings/${listingId}/landmark`,
      name: '주변명소 및 추천한옥설정',
      children: (
        <div>
          <div>
            현재{' '}
            <span style={{ fontWeight: '500', color: 'var(--point-color)' }}>
              {landmarks?.length}개의
            </span>
            <span> 주변명소 및 </span>
            <span style={{ fontWeight: '500', color: 'var(--point-color)' }}>
              {recommendListings?.length}개의
            </span>
            <span> 추천한옥이 있습니다.</span>
          </div>
        </div>
      ),
    },
    {
      route: `/admin/listings/${listingId}/settings`,
      name: '시설설정',
      children: (
        <div>
          <div style={{ fontWeight: '600' }}>운영설정 및 삭제 등 설정화면</div>
          <div>
            현재{' '}
            <span style={{ fontWeight: '500', color: 'var(--point-color)' }}>
              {listing?.isActive ? '운영중' : '운영 대기중'}
            </span>
            인 시설입니다.
          </div>
        </div>
      ),
    },
  ];

  const fetchAmenities = async () => {
    const result = await ServiceHelper.adminListingService?.getAmenitiesByListing1(
      Number(listingId),
    );
    setAmenities(result?.data as AmenityModel[]);
  };

  const fetchTags = async () => {
    const result = await ServiceHelper.adminListingService?.getTagsByListing1(Number(listingId));
    setTags(result?.data as TagModel[]);
  };

  const fetchPhotos = async () => {
    const result = await ServiceHelper.adminListingService?.getListingImages(Number(listingId));
    setPhotos(result?.data as AdminListingImageDto[]);
  };

  const fetchLandmarkAndRecommendListings = () => {
    ServiceHelper.adminListingService?.getLandmarksByListing1(Number(listingId)).then((res) => {
      setLandmarks(res.data as AdminLandmarkDto[]);
    });
    ServiceHelper.adminListingService?.getRelatedListings1(Number(listingId)).then((res) => {
      setRecommendListings(res.data as AdminListingDto[]);
    });
  };

  const fetchLanguage = async () => {
    ServiceHelper.adminLocaleService?.getLocales1().then((res) => {
      setLanguages(res.data as AdminSystemLocaleDto[]);
    });
  };

  const fetchOptions = () => {
    ServiceHelper.adminListingService?.getOptionsByListing1(Number(listingId)).then((res) => {
      setOptions(res.data as AdminOptionDto[]);
    });
  };

  useEffect(() => {
    if (receiver?.id === 'reload' && receiver?.param === 'amenities') {
      fetchAmenities();
    }
    if (receiver?.id === 'reload' && receiver?.param === 'photos') {
      fetchPhotos();
    }

    if (receiver?.id === 'reload' && receiver?.param === 'tags') {
      fetchTags();
    }
    if (receiver?.id === 'reload' && receiver?.param === 'landmark') {
      fetchLandmarkAndRecommendListings();
    }

    if (receiver?.id === 'reload' && receiver?.param === 'landmark') {
      fetchLandmarkAndRecommendListings();
    }
    if (receiver?.id === 'reload' && receiver?.param === 'recommend') {
      fetchLandmarkAndRecommendListings();
    }

    if (receiver?.id === 'reload' && receiver?.param === 'language') {
      fetchLanguage();
    }
    if (receiver?.id === 'reload' && receiver?.param === 'option') {
      fetchOptions();
    }
  }, [receiver]);

  useEffect(() => {
    if (listingId) {
      ServiceHelper.adminListingService?.getListing(Number(listingId)).then((res) => {
        if (res.status === 200) {
          setListing(res?.data);
        }
        setListing(res?.data);
      });

      fetchAmenities();
      fetchPhotos();
      fetchTags();
      fetchLandmarkAndRecommendListings();
      fetchLanguage();
      fetchOptions();
    }
  }, [listingId]);

  return (
    <div>
      <Row justifyContent="flex-start" padding="20px 0" gap={10}>
        <IconButton style={{ background: '#efefef' }} onClick={() => navigate('/admin/listings')}>
          <ArrowBackIcon />
        </IconButton>
        <Text type="H3">{listing?.localizedName?.ko}</Text>
      </Row>
      <Row gap={10} flexDirection="column" width="100%">
        {routes.map((route) => (
          <div key={route.route} style={{ width: '300px' }}>
            <Box
              title={route.name}
              selected={location.pathname === route.route}
              onClick={() => {
                if (route.route) {
                  navigate(route.route);
                }
              }}
            >
              <Text type="H5" buttonRole="DESCRIPTION">
                {route.children}
              </Text>
            </Box>
          </div>
        ))}
      </Row>
    </div>
  );
};

export default ListingMenus;
